/**
 * Hook to sync organization context with the mention system
 *
 * This hook ensures that recent mentions are namespaced by organization,
 * so users don't see mentions from other organizations they may have access to.
 * It also clears all recent mentions on logout.
 *
 * @example
 * // In App.tsx or a high-level component:
 * useMentionOrganizationSync();
 */
import { useEffect, useRef } from 'react';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { useAuthStore } from '@/stores/auth/auth-store';
import { mentionSystem } from '@/services/ai/mentions';

export function useMentionOrganizationSync(): void {
  const currentContext = useOrganizationStore((state) => state.currentContext);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const wasAuthenticated = useRef(isAuthenticated);

  // Sync organization context for localStorage namespacing
  useEffect(() => {
    // Use the organization ID if in org context, null for personal context
    const orgId = currentContext?.type === 'organization' ? currentContext.id : null;
    mentionSystem.setOrganizationContext(orgId);
  }, [currentContext]);

  // Clear all recent mentions on logout
  useEffect(() => {
    // Detect logout transition (was authenticated, now not)
    if (wasAuthenticated.current && !isAuthenticated) {
      mentionSystem.clearAllRecentMentions();
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);
}
